import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ReactFlow, Background, Controls, MiniMap, Handle, Position,
  type Node, type Edge, NodeProps, MarkerType,
} from "@xyflow/react";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type TNode = {
  id: string; label: string; kind: "outcome"|"driver"|"lever"|"input";
  value: number; unit: string; delta_pct: number;
  children: string[]; parent: string | null; explanation: string;
};
type Seeded = {
  tree_id: string; root_kpi_id: string; root_label: string;
  nodes: TNode[]; edges: { source: string; target: string; weight: number }[];
};

const KPIS = [
  { id: "revenue_monthly", label: "Umsatz / Monat" },
  { id: "ebitda_margin", label: "EBITDA Marge" },
  { id: "dso", label: "Days Sales Outstanding" },
];

function fmt(v: number, unit: string): string {
  if (unit === "EUR") return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
  if (unit === "%") return v.toFixed(1) + " %";
  if (unit === "days") return Math.round(v) + " T";
  if (unit === "count") return Math.round(v).toString();
  return v.toString();
}

function TreeNode({ data }: NodeProps<any>) {
  const n = data.node as TNode;
  const positive = (n.kind === "outcome" || n.kind === "driver") ? n.delta_pct >= 0 : n.delta_pct < 0;
  return (
    <div className={`dt-node kind-${n.kind} ${(data as any).active ? "active" : ""}`} onClick={() => (data as any).onPick(n.id)}>
      <Handle type="target" position={Position.Top} style={{ background: "var(--v2-cyan)" }} />
      <div className="dt-kind">{n.kind}</div>
      <div className="dt-label">{n.label}</div>
      <div className="dt-value">{fmt(n.value, n.unit)}</div>
      <div className={`dt-delta ${positive ? "up" : "down"}`}>{positive ? "▲" : "▼"} {Math.abs(n.delta_pct).toFixed(1)}%</div>
      <Handle type="source" position={Position.Bottom} style={{ background: "var(--v2-cyan)" }} />
    </div>
  );
}

const nodeTypes: any = { tnode: TreeNode };

function layout(seeded: Seeded): { nodes: Node[]; edges: Edge[] } {
  // simple BFS layered layout by depth
  const byId: Record<string, TNode> = Object.fromEntries(seeded.nodes.map((n) => [n.id, n]));
  const depth: Record<string, number> = {};
  const rootId = seeded.root_kpi_id;
  depth[rootId] = 0;
  let frontier = [rootId];
  while (frontier.length) {
    const next: string[] = [];
    for (const id of frontier) {
      const node = byId[id];
      for (const c of node.children) {
        if (depth[c] === undefined) { depth[c] = depth[id] + 1; next.push(c); }
      }
    }
    frontier = next;
  }
  const byDepth: Record<number, string[]> = {};
  for (const [id, d] of Object.entries(depth)) {
    (byDepth[d] ||= []).push(id);
  }
  const COL_W = 250;
  const ROW_H = 160;
  const nodes: Node[] = [];
  for (const [d, ids] of Object.entries(byDepth)) {
    const dd = Number(d);
    const total = ids.length;
    ids.forEach((id, i) => {
      const offset = ((i - (total - 1) / 2)) * COL_W;
      nodes.push({
        id, position: { x: 400 + offset, y: 80 + dd * ROW_H },
        data: { node: byId[id] }, type: "tnode",
      });
    });
  }
  const edges: Edge[] = seeded.edges.map((e, i) => ({
    id: `e_${i}`, source: e.source, target: e.target, animated: true,
    style: { stroke: "rgba(56,205,234,0.55)", strokeWidth: Math.max(1, Math.min(3, e.weight * 3)) },
    markerEnd: { type: MarkerType.ArrowClosed, color: "#38cdea" },
  }));
  return { nodes, edges };
}

export default function DriverTreeCanvasPage() {
  const params = useParams<{ kpiId?: string }>();
  const nav = useNavigate();
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const [picked, setPicked] = useState<string | null>(null);
  const kpiId = params.kpiId || "revenue_monthly";

  const q = useQuery({
    queryKey: ["tree", effectiveTenantId, kpiId],
    queryFn: () => api.get<Seeded>(`/driver-trees/seeded/${kpiId}`),
  });

  // Echte Bäume dieses Mandanten — damit die Beispielansicht ehrlich auf die
  // Live-Daten verweist, sobald es sie gibt (UI/UX Iteration 3).
  const realTrees = useQuery({
    queryKey: ["driver-trees", effectiveTenantId],
    queryFn: () => api.get<Array<{ id: string; name?: string }>>("/driver-trees"),
    retry: 0,
    staleTime: 60_000,
  });
  const realTree = (realTrees.data ?? [])[0];

  const { nodes, edges } = useMemo(() => {
    if (!q.data) return { nodes: [] as Node[], edges: [] as Edge[] };
    const laid = layout(q.data);
    return {
      nodes: laid.nodes.map((n) => ({
        ...n,
        data: { node: (n.data as any).node, onPick: setPicked, active: picked === n.id },
      })),
      edges: laid.edges,
    };
  }, [q.data, picked]);

  const pickedNode = q.data?.nodes.find((n) => n.id === picked);

  return (
    <section className="dt-page">
      <div className="demo-notice">
        <strong>Beispiel-Ansicht mit Musterdaten.</strong>
        <span>
          Diese Seite zeigt einen generischen Beispiel-Baum (Umsatz, EBITDA, DSO), nicht Ihre Daten.
          Ihren echten Driver-Tree mit Ist-Werten, Zielen und Arbeitspaketen finden Sie unter{" "}
          <Link to="/app/trees">Driver-Trees</Link>.
        </span>
        {realTree && (
          <Link to="/app/trees" className="btn primary small" title="Öffnet die Wirkungslandkarte mit Ihren echten Daten.">
            Echten Baum öffnen{realTree.name ? `: ${realTree.name}` : ""}
          </Link>
        )}
      </div>
      <header className="dt-hero">
        <div>
          <p className="hero-eyebrow">Beispiel-Baum · Ursache &amp; Wirkung</p>
          <h1>{q.data?.root_label || "Driver-Tree"}</h1>
          <p className="muted">Outcome → Treiber → Hebel → Inputs · klicken Sie einen Knoten für KI-Beratung.</p>
        </div>
        <div className="dt-kpi-switch">
          {KPIS.map((k) => (
            <button key={k.id} className={`mode-pill ${k.id === kpiId ? "active" : ""}`} onClick={() => nav(`/app/trees/${k.id}`)}>
              <strong>{k.label}</strong>
            </button>
          ))}
        </div>
      </header>

      <div className="dt-canvas-wrap">
        <div className="dt-canvas">
          {q.isLoading && <p className="muted">Lade Tree …</p>}
          {q.data && (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              fitView
              minZoom={0.3}
              maxZoom={1.5}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="rgba(255,255,255,0.06)" gap={24} />
              <Controls />
              <MiniMap nodeColor={(n: any) => {
                const k = (n.data as any).node.kind;
                return k === "outcome" ? "#38cdea" : k === "driver" ? "#4d9bff" : k === "lever" ? "#35c88a" : "#e2a93c";
              }} maskColor="rgba(8,9,15,0.7)" />
            </ReactFlow>
          )}
        </div>
        <aside className="dt-side">
          {pickedNode ? (
            <>
              <p className="hero-eyebrow">{pickedNode.kind}</p>
              <h3>{pickedNode.label}</h3>
              <p className="muted">{pickedNode.explanation}</p>
              <div className="dt-side-stats">
                <div><span className="muted small">Wert</span><strong>{fmt(pickedNode.value, pickedNode.unit)}</strong></div>
                <div><span className="muted small">Veränderung</span><strong>{pickedNode.delta_pct >= 0 ? "+" : ""}{pickedNode.delta_pct.toFixed(1)}%</strong></div>
              </div>
              <div className="dt-side-actions">
                <Link
                  to={`/app/advisor?msg=${encodeURIComponent(`Was treibt ${pickedNode.label} mit aktuellem Wert ${fmt(pickedNode.value, pickedNode.unit)} und Veränderung ${pickedNode.delta_pct.toFixed(1)}%? Welche Hebel sollten wir priorisieren?`)}&mode=strategy&persona=CFO`}
                  className="btn primary"
                >
                  KI-Berater fragen
                </Link>
              </div>
            </>
          ) : (
            <>
              <p className="hero-eyebrow">Tipp</p>
              <h3>Knoten klicken</h3>
              <p className="muted">Wählen Sie einen Knoten im Baum, um Details zu sehen und den KI-Berater mit Kontext zu starten.</p>
            </>
          )}
          <div className="dt-legend">
            <p className="muted small">Legende</p>
            <div className="dt-legend-row"><span className="dt-legend-swatch outcome"></span> Outcome — Zielkennzahl</div>
            <div className="dt-legend-row"><span className="dt-legend-swatch driver"></span> Driver — direkter Treiber</div>
            <div className="dt-legend-row"><span className="dt-legend-swatch lever"></span> Lever — steuerbar</div>
            <div className="dt-legend-row"><span className="dt-legend-swatch input"></span> Input — Datenquelle</div>
          </div>
        </aside>
      </div>
    </section>
  );
}
